import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'; // ou ton système de modal
import { useAuth } from '@/contexts/AuthContext';
import { BASE_URL } from '@/config/api';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import  { useLanguage } from '@/contexts/LanguageContext';

const NOTIF_KEY = 'read_notifications';
const EXPIRATION_DAYS = 30;

function loadReadNotifications(): Record<string, string> {
  try {
    const raw = localStorage.getItem(NOTIF_KEY);
    const stored: Record<string, string> = raw ? JSON.parse(raw) : {};
    const now = Date.now();
    const cutoff = now - EXPIRATION_DAYS * 24 * 60 * 60 * 1000;

    const valid = Object.fromEntries(
      Object.entries(stored).filter(([_, date]) => {
        const timestamp = new Date(date).getTime();
        return !isNaN(timestamp) && timestamp > cutoff;
      })
    );

    localStorage.setItem(NOTIF_KEY, JSON.stringify(valid));
    return valid;
  } catch {
    return {};
  }
}


function markNotificationAsRead(id: number) {
  const read = loadReadNotifications();
  read[id] = new Date().toISOString();
  localStorage.setItem(NOTIF_KEY, JSON.stringify(read));
}

export function NotificationBell() {
  const { token } = useAuth();
  const [openDropdown, setOpenDropdown] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState<any>(null);
  const { t } = useLanguage();

  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications', token],
    queryFn: async () => {
      const response = await fetch(`${BASE_URL}/api/notifications`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
        },
      });
      const json = await response.json();
      return json.notifications || [];
    },
  });

  const read = loadReadNotifications();
  const unreadNotifications = notifications.filter((n) => !read[n.id]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notif: any) => {
    markNotificationAsRead(notif.id);
    setSelectedNotif(notif);
    setOpenDropdown(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button variant="ghost" size="icon" onClick={() => setOpenDropdown(!openDropdown)}>
        <Bell className="text-foreground" />
        {unreadNotifications.length > 0 && (
          <span className="absolute -top-1 right-0 bg-blue-400 rounded-full text-xs w-4 h-4 flex items-center justify-center text-white">
            {unreadNotifications.length}
          </span>
        )}
      </Button>

      {openDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white border rounded-md shadow-lg z-50">
          {unreadNotifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {t('notifications.noNotifications')}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
              {unreadNotifications.map((notif) => (
                <li
                  key={notif.id}
                  className="p-3 hover:bg-gray-100 cursor-pointer rounded-md"
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className="font-medium">{notif.title}</div>
                  <div className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {selectedNotif && (
        <Dialog open={!!selectedNotif} onOpenChange={() => setSelectedNotif(null)}>
          <DialogContent>
            <DialogTitle>{selectedNotif.title}</DialogTitle>
            <div className="mt-2 text-sm whitespace-pre-wrap">{selectedNotif.message}</div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
